import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAnalytics } from '@/hooks/useAnalytics';
import { LandingButton } from './LandingButton';

const ArrowRightIcon = ({ size = 16 }: { size?: number }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 12h14" />
    <path d="m12 5 7 7-7 7" />
  </svg>
);

const STEP_NUMBERS = ['01', '02', '03'];

export const HowItWorksSection = React.memo(() => {
  const { t } = useTranslation();
  const { track } = useAnalytics();

  const steps = STEP_NUMBERS.map((n, i) => ({
    number: n,
    title: t(`landing.howItWorks.step${i + 1}Title`),
    description: t(`landing.howItWorks.step${i + 1}Description`),
    highlight: t(`landing.howItWorks.step${i + 1}Highlight`),
  }));

  return (
    <section id="como-funciona" className="py-24 px-6 bg-gray-50/60 border-y border-gray-100">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <span className="text-xs font-semibold uppercase tracking-widest text-gray-500">{t('landing.howItWorks.eyebrow')}</span>
          <h2
            className="text-3xl md:text-5xl font-medium mt-3 mb-5"
            style={{
              background: 'linear-gradient(to bottom, #000000, #000000, rgba(0, 0, 0, 0.6))',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              letterSpacing: '-0.04em',
            }}
          >
            {t('landing.howItWorks.titleLine1')}
            <br />
            {t('landing.howItWorks.titleLine2')}
          </h2>
          <p className="text-base max-w-2xl mx-auto text-gray-500">{t('landing.howItWorks.subtitle')}</p>
        </div>

        <div className="relative">
          <div
            className="hidden md:block absolute top-12 left-[16.6%] right-[16.6%] h-px"
            style={{ background: 'linear-gradient(to right, transparent, #d1d5db, #d1d5db, transparent)' }}
            aria-hidden="true"
          />
          <div className="grid md:grid-cols-3 gap-8 md:gap-6 relative">
            {steps.map((step) => (
              <div key={step.number} className="relative">
                <div className="flex flex-col items-center text-center">
                  <div className="relative z-10 mb-6">
                    <div className="w-24 h-24 rounded-full bg-white border border-gray-200 shadow-sm flex items-center justify-center">
                      <span
                        className="text-2xl font-semibold tracking-tight"
                        style={{
                          background: 'linear-gradient(to bottom, #000000, rgba(0, 0, 0, 0.5))',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          backgroundClip: 'text',
                        }}
                      >
                        {step.number}
                      </span>
                    </div>
                  </div>
                  <h3 className="text-xl font-semibold mb-3 text-black">{step.title}</h3>
                  <p className="text-sm leading-relaxed mb-4 max-w-xs text-gray-500">{step.description}</p>
                  <span className="inline-flex items-center gap-1.5 text-[11px] font-medium px-3 py-1.5 rounded-full bg-white border border-gray-200 text-gray-700">
                    <span className="w-1.5 h-1.5 rounded-full bg-black" />
                    {step.highlight}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-16 flex flex-col items-center gap-3">
          <Link
            to="/auth?tab=signup"
            onClick={() => track('landing_cta_clicked', { location: 'how_it_works' })}
          >
            <LandingButton type="button" variant="gradient" size="lg" className="rounded-lg">
              {t('landing.howItWorks.cta')}
              <ArrowRightIcon size={16} />
            </LandingButton>
          </Link>
          <p className="text-xs text-gray-400">{t('landing.howItWorks.ctaDisclaimer')}</p>
        </div>
      </div>
    </section>
  );
});
HowItWorksSection.displayName = 'HowItWorksSection';
